import {
	AfterContentChecked,
	AfterContentInit,
	Component,
	OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import {
	BsDatepickerConfig,
	BsDatepickerViewMode
} from 'ngx-bootstrap/datepicker';
import { forkJoin } from 'rxjs';
import { FacadeService } from 'src/app/core/services/facade.service';
import { LogService } from 'src/app/core/services/log/log.service';
import { ErrorResponse } from 'src/app/shared/models/api-response/error-response';
import { Employee } from 'src/app/shared/models/employee/employee';
import { Funder } from 'src/app/shared/models/funder';
import { FunderReportWeekly } from 'src/app/shared/models/funder-report-weekly';
import { DailyReport } from 'src/app/shared/models/statistics/daily-report';
import { DateUtils } from 'src/app/shared/utils/date-utils';

@Component({
	selector: 'app-reports',
	templateUrl: './reports.component.html',
	styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit {
	/**
	 * Elements de la tabs bar de la page report
	 */
	tabs: { id: number; name: string }[] = [];
	currentTabId: number;

	/* Sélecteur de dates */
	selectedWeek: { value: number } = { value: null };
	selectedYear: { value: number } = { value: null };

	/* À transférer au composant Funder */
	funderReport: FunderReportWeekly;
	funders: Funder[];

	/* Utiliser pour manipuler les stats */
	isSESSAD = false;
	isSAAD = false;
	isSSIAD = false;

	/* En attente d'une réponse du back pour récupérer les stats */
	loading: boolean;

	/* Les statistiques globale */
	statsDataAvailable: boolean = false; // si il y en a ou non
	globalStats: any;
	lpcStats: any;
	lsfStats: any;
	pdnStats: any;
	unassignedStats: any;
	oralStats: any;
	stats: any;

	/* Les statistiques de l'activity report */
	reportStatsDataAvailable: boolean = false; // si il y en a ou non
	statsEmployee: any = [];
	statsBeneficiaire: any[] = [];

	/* Les stats journalières */
	dailyReports: DailyReport[] = [];

	/* Pour le rapport par mois */
	monthDatePickerValue: Date = new Date();
	minMode: BsDatepickerViewMode = 'month';
	bsConfig?: Partial<BsDatepickerConfig>;

	/* Pour switcher la vue rapport mensuel/hebdo */
	currentReportTypeTabId: number = 1;
	reportTypeTabs: { id: number; name: string }[];

	employees: Employee[];

	constructor(
		private facade: FacadeService,
		private logger: LogService,
		private dateUtils: DateUtils
	) {
		this.currentTabId = 1;
		this.loading = true;

		this.reportTypeTabs = [
			{
				id: 1,
				name: 'Rapports par mois'
			},
			{
				id: 2,
				name: 'Rapports par semaine'
			}
		];

		this.tabs = [
			{
				id: 1,
				name: "Rapport d'activité"
			},
			{
				id: 2,
				name: 'Statistiques'
			},
			{
				id: 5,
				name: 'Suivi DH'
			},
			{
				id: 4,
				name: 'Suivi des congés'
			}
		];

		this.bsConfig = Object.assign(
			{},
			{
				minMode: this.minMode,
				dateInputFormat: 'MMMM'
			}
		);
	}

	ngOnInit(): void {
		this.isSESSAD = this.facade.dataStorageService.isSESSAD();
		this.isSAAD = this.facade.dataStorageService.isSAAD();
		this.isSSIAD = this.facade.dataStorageService.isSSIAD();

		if (this.facade.dataStorageService.isFunderActive()) {
			this.tabs.push({
				id: 3,
				name: 'Rapport financeur'
			});
		}
	}

	isDataUnavailable(): boolean {
		return (
			(((!this.reportStatsDataAvailable || !this.statsDataAvailable) &&
				(this.isReportTypeTabActive(2) || !this.isTabActive(1))) ||
				(this.statsBeneficiaire.length === 0 &&
					this.isReportTypeTabActive(1))) &&
			!this.isTabActive(4)
		);
	}

	changeActiveTab(tabId: number) {
		this.currentTabId = tabId;
		if (this.isTabActive(4)) {
			this.getAllActiveEmployees();
		}
	}

	getAllActiveEmployees() {
		if (!this.selectedYear.value) {
			this.selectedYear.value = this.monthDatePickerValue.getFullYear();
		}
		this.facade.employeeService
			.getAllActiveEmployeesWithVacationsForYear(this.selectedYear.value)
			.subscribe({
				next: (employees: Employee[]) => {
					this.employees = employees;

					this.loading = false;
				},
				error: (err) => {
					this.logger.log(
						'' +
							err.status +
							' ' +
							(err.error.response ? err.error.response : '')
					);
					this.loading = false;
					this.statsDataAvailable = false;
				}
			});
	}

	isTabActive(tabId: number) {
		return tabId === this.currentTabId;
	}

	changeActiveReportTypeTab(reportTypeTabId: number) {
		this.currentReportTypeTabId = reportTypeTabId;
	}

	isReportTypeTabActive(reportTypeTabId: number) {
		return reportTypeTabId === this.currentReportTypeTabId;
	}

	initGlobalReport() {
		this.facade.statistiqueService
			.getStatisticsByYearAndWeekNumber(
				this.selectedYear.value,
				this.selectedWeek.value
			)
			.subscribe({
				next: (stats: any) => {
					this.statsDataAvailable =
						stats.beanCategories[0].numberFormation > 0 ? true : false;

					const statsByCategory = stats.beanCategories;
					this.stats = statsByCategory;
					this.globalStats = statsByCategory.filter(
						(category) => category.nameCategorie === 'Global'
					)[0];
					this.lpcStats = statsByCategory[1];
					this.lsfStats = statsByCategory[2];
					this.pdnStats = statsByCategory[3];
					this.oralStats = statsByCategory[4];
					this.unassignedStats = statsByCategory.filter(
						(category) => category.nameCategorie === 'Sans assignation'
					)[0];

					this.loading = false;
				},
				error: (err) => {
					this.logger.log(
						'' +
							err.status +
							' ' +
							(err.error.response ? err.error.response : '')
					);
					this.loading = false;
					this.statsDataAvailable = false;
				}
			});
	}

	initDailyReport() {
		this.facade.statistiqueService
			.getDailyStatisticsReportByYearAndWeekNumber(
				this.selectedYear.value,
				this.selectedWeek.value
			)
			.subscribe({
				next: (dailyReportsList: DailyReport[]) => {
					this.dailyReports = dailyReportsList;
					this.dailyReports = this.dailyReports.filter(
						(report) => report.numberOfMission > 0
					);

					// On doit itérer à cause d'un décalage d'1h entre les dates du back et celles du front
					// "client doesn't know the time is UTC+1, so client will just automatically assume you are in UTC-0"
					// => fix : new Date() pour forcer le client à prendre le UTC+1 en compte
					for (let reports of this.dailyReports) {
						reports.date = new Date(reports.date);
					}
				},
				error: (err) => {
					this.logger.error(err);
				},
				complete: () => {
					this.loading = false;
				}
			});
	}

	initMonthReportBeneficiaire() {
		// +1 au mois car le pickerValue part de Janvier = 0
		this.facade.statistiqueService
			.getActivityReportByBeneficiaireByYearAndMonth(
				this.monthDatePickerValue.getFullYear(),
				this.monthDatePickerValue.getMonth() + 1
			)
			.subscribe({
				next: (data: any) => {
					this.statsBeneficiaire = data;
				},
				error: (err) => {
					this.logger.error(err);
				},
				complete: () => {
					this.loading = false;
				}
			});
	}

	initMonthReportEmployee() {
		// +1 au mois car le pickerValue part de Janvier = 0
		this.facade.statistiqueService
			.getActivityReportByEmployeeByYearAndMonth(
				this.monthDatePickerValue.getFullYear(),
				this.monthDatePickerValue.getMonth() + 1
			)
			.subscribe({
				next: (data: any) => {
					//console.log('=== Données reçues du backend ===', data);
					this.statsEmployee = data;
				},
				error: (err) => {
					this.logger.error(err);
				},
				complete: () => {
					this.loading = false;
				}
			});
	}

	initActivityReport() {
		forkJoin({
			statsByEmployee:
				this.facade.statistiqueService.getActivityReportByEmployeeByYearAndWeekNumber(
					this.selectedYear.value,
					this.selectedWeek.value
				),
			statsByBeneficiaire:
				this.facade.statistiqueService.getActivityReportByBeneficiaireByYearAndWeekNumber(
					this.selectedYear.value,
					this.selectedWeek.value
				)
		}).subscribe(
			(result: { statsByEmployee: any[]; statsByBeneficiaire: any[] }) => {
				this.logger.log('Activity-report init success');
				if (this.isSESSAD) {
					this.reportStatsDataAvailable =
						result.statsByEmployee.slice(-5)[0] &&
						result.statsByEmployee.slice(-5)[0].numberFormation > 0 // slice(-4) = stats Globales de l'array
							? true
							: false;
				} else if (this.isSAAD) {
					this.reportStatsDataAvailable =
						result.statsByEmployee.slice(-1)[0] &&
						result.statsByEmployee.slice(-1)[0].numberFormation > 0 // slice(-4) = stats Globales de l'array
							? true
							: false;
				}
				this.statsEmployee = result.statsByEmployee;
				this.statsBeneficiaire = result.statsByBeneficiaire;
			},
			(err) => {
				this.logger.error(err);
				this.loading = false;
			}
		);

		this.facade.statistiqueService
			.getFunderReport(this.selectedYear.value, this.selectedWeek.value)
			.subscribe({
				next: (funderReport: FunderReportWeekly) => {
					if (funderReport && funderReport.employeesReport.length > 0) {
						this.funderReport = funderReport;
						this.funders = this.funderReport.employeesReport
							.flatMap((employeeReport) => employeeReport.days)
							.flatMap((dailyReport) => dailyReport.funderReports)
							.map((funderReport) => funderReport.funder);

						this.funderReport.employeesReport.forEach((empReport) => {
							empReport.days.sort(
								(a, b) =>
									new Date(a.date).getTime() - new Date(b.date).getTime()
							);
						});
						this.funders = this.uniqBy(this.funders, JSON.stringify);
					} else {
						this.funderReport = null;
					}
				},
				error: (err: ErrorResponse) => {
					this.logger.error(err + '');
				}
			});
	}

	uniqBy(a, key) {
		let seen = {};
		return a.filter(function (item) {
			let k = key(item);
			return seen.hasOwnProperty(k) ? false : (seen[k] = true);
		});
	}

	updateWeekReportData() {
		this.facade.solutionService
			.evaluateSolution(this.selectedYear.value, this.selectedWeek.value)
			.subscribe({
				complete: () => {
					this.initActivityReport();
					this.initDailyReport();
					this.initGlobalReport();
				},
				error: (err: ErrorResponse) => {
					this.logger.debug(err.error + '');
					this.loading = false;
				}
			});
	}

	updateMonthData() {
		// Suppression les stats précédentes pour refresh
		this.statsBeneficiaire = [];
		this.statsEmployee = [];
		this.initMonthReportBeneficiaire();
		this.initMonthReportEmployee();
	}
}
